import React from 'react'
import PropTypes from 'prop-types'
import Loading from '../../../molecules/Loading'
import { useDDPReportContext } from './DDPReportContext'

/**
 * Loading component for DDP Reports
 * Shows loading state with progress, hides children until ready
 * Must be used within <DDPReportProvider>
 */
function DDPReportLoader ({
  loadingMessage,
  loadingComponent,
  errorComponent,
  children
}) {
  const { isLoading, error, loadingProgress } = useDDPReportContext()

  // Show error state
  if (error) {
    if (errorComponent) {
      return errorComponent
    }

    return (
      <div className='flex flex-col items-center justify-center p-8 opal-surface-default radius-md'>
        <div className='text-xl mb-2'>⚠️ Error Loading Report</div>
        <div className='text-sm opal-text-secondary'>
          {error.message || 'An error occurred while loading the report data.'}
        </div>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    if (loadingComponent) {
      return loadingComponent
    }

    return (
      <div className='flex flex-col items-center justify-center p-8 opal-surface-default radius-md'>
        <div className='mb-4'>
          <Loading />
        </div>

        <div className='text-lg font-size-md mb-2'>
          {loadingMessage || 'Loading Report Data'}
        </div>

        {loadingProgress && (
          <>
            <div className='text-sm opal-text-secondary mb-2'>
              {loadingProgress.message}
            </div>
            {loadingProgress.totalRows > 0 && (
              <div className='text-xs opal-text-tertiary'>
                {Math.round((loadingProgress.rowsLoaded / loadingProgress.totalRows) * 100)}% complete
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // Show children when loaded
  return <>{children}</>
}

DDPReportLoader.propTypes = {
  /** Message to show while loading */
  loadingMessage: PropTypes.string,

  /** Custom loading component to render instead of default */
  loadingComponent: PropTypes.node,

  /** Custom error component to render instead of default */
  errorComponent: PropTypes.node,

  /** Children to render when data is loaded */
  children: PropTypes.node
}

export default DDPReportLoader
